import { findById } from '@qi/frequency-db';
import { copy } from '@qi/shared';
import { buttonClasses, Chip, ResonanceMark, Skeleton } from '@qi/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Play, Star } from 'lucide-react';
import { useSessions } from '../lib/queries.ts';

export const Route = createFileRoute('/_app/session/')({
  component: SessionsListPage,
});

function SessionsListPage() {
  const sessionsQuery = useSessions();
  const sessions = sessionsQuery.data?.sessions ?? [];
  const isLoading = sessionsQuery.isLoading;
  const isEmpty = !isLoading && sessions.length === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-10 px-6 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Rebalance</p>
          <h1 className="mt-2 font-display text-4xl text-ink-0">Sessions</h1>
        </div>
        <Link
          to="/rebalance"
          className={`${buttonClasses({ variant: 'resonance', size: 'lg' })} group`}
        >
          <Play size={16} strokeWidth={2} aria-hidden="true" />
          <span>Start rebalance</span>
        </Link>
      </header>

      {isEmpty ? (
        <EmptyHistory />
      ) : isLoading ? (
        <div className="space-y-3">
          <Skeleton width="100%" height={80} rounded="lg" />
          <Skeleton width="100%" height={80} rounded="lg" />
          <Skeleton width="100%" height={80} rounded="lg" />
        </div>
      ) : (
        <section className="space-y-3">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
            Recent sessions
          </p>
          <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
            {sessions.map((session, idx) => {
              const firstFreq = findById(session.protocol[0]?.frequencyId ?? '');
              const totalMs = session.protocol.reduce((s, p) => s + p.durationMs, 0);
              return (
                <div
                  key={session.id}
                  className={`flex flex-col gap-3 px-6 py-4 md:flex-row md:items-center md:justify-between ${
                    idx > 0 ? 'border-t border-hairline' : ''
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-sm text-ink-0">
                      {formatDate(session.startedAt)}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
                      {Math.round(totalMs / 60_000)} min · {session.protocol.length} steps ·{' '}
                      {session.protocol[0]?.playbackStyle}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Chip withDot tone="aurora">
                      {firstFreq?.historicalAssociation ?? session.protocol[0]?.frequencyId}
                    </Chip>
                    {session.rating && (
                      <span className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.18em] text-aurora">
                        <Star size={12} strokeWidth={2} className="fill-current" />
                        {session.rating} / 5
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-hairline bg-surface-1 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ResonanceMark size={28} className="text-aurora" aria-hidden="true" />
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-aurora">Modes</p>
              <p className="text-sm text-ink-1">
                {copy.session.modes.ambient} · {copy.session.modes['pulsed-harmonic']} ·{' '}
                {copy.session.modes.binaural}
              </p>
            </div>
          </div>
          <Link to="/rebalance" className={buttonClasses({ variant: 'ghost' })}>
            Open rebalance →
          </Link>
        </div>
      </section>
    </div>
  );
}

function EmptyHistory() {
  return (
    <div className="flex flex-col items-center gap-6 rounded-lg border border-hairline bg-surface-1 px-6 py-16 text-center">
      <div className="text-aurora/40">
        <ResonanceMark size={96} aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 className="font-display text-2xl text-ink-0">No sessions yet.</h2>
        <p className="max-w-md text-ink-1">
          Run a scan to receive a protocol, then press play. Your sessions will show up here.
        </p>
      </div>
      <Link to="/scan/bioscan" className={buttonClasses({ variant: 'resonance' })}>
        Begin a scan
      </Link>
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
